import { Id, Loading } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import { useParams } from "react-router";
import styled from "styled-components";
import { contactService } from "../../shared/api/contactService";
import { useDataFieldsQuery } from "../../shared/api/dataFieldService";
import { useContactView } from "../../shared/hooks/useContactView";
import { StyledSection } from "../../shared/view";
import { ArchivedContactBanner } from "./ArchivedContactBanner";
import { ContactViewAddressSection } from "./ContactViewAddressSection";
import { ContactViewCompaniesSection } from "./ContactViewCompaniesSection";
import { ContactViewEmailAddressSection } from "./ContactViewEmailAddressSection";
import { ContactViewGeneralSection } from "./ContactViewGeneralSection";
import { ContactViewPhoneNumberSection } from "./ContactViewPhoneNumberSection";
import { ContactViewTagsSection } from "./ContactViewTagsSection";

export const StyledStyledSection = styled(StyledSection)`
	grid-template-areas:
		"general general"
		"companies companies"
		"email   email"
		"phone   phone"
		"address address"
		"tags tags";

	${({ theme }) => theme.breakpoints.tablet} {
		margin-top: 0;
		grid-template-areas:
			"general general"
			"companies email"
			"address phone"
			"tags tags";
	}
`;

export const ContactViewPage: FC = () => {
	const { currentContactView, isLoading } = useContactView();

	if (isLoading) {
		return <Loading />;
	}

	if (!currentContactView) {
		return null;
	}

	return <ContactViewContent contactViewId={currentContactView.id} />;
};

const ContactViewContent: FC<{ contactViewId: Id }> = ({ contactViewId }) => {
	const { id = "" } = useParams<"id">();
	const { data: dataFields, isLoading: isDataFieldsLoading } = useDataFieldsQuery();
	const { data: contact, isLoading: isContactLoading } = useQuery({
		queryKey: [...contactService.showKey(id), `${contactViewId}`],
		queryFn: () => contactService.show(id, `${contactViewId}`).then((x) => x.data.data),
	});

	const isLoading = isContactLoading || isDataFieldsLoading;

	return (
		<>
			{contact && contact.isArchived && <ArchivedContactBanner id={contact.id} />}

			<StyledStyledSection color="content">
				{isLoading || !contact ? (
					<Loading />
				) : (
					<>
						<ContactViewGeneralSection contact={contact} dataFields={dataFields} contactViewId={contactViewId} />
						<ContactViewEmailAddressSection contact={contact} />
						<ContactViewPhoneNumberSection contact={contact} />
						<ContactViewAddressSection contact={contact} />
						<ContactViewCompaniesSection contact={contact} />
						<ContactViewTagsSection contact={contact} />
					</>
				)}
			</StyledStyledSection>
		</>
	);
};
